import client from './client';

// --- Auth -------------------------------------------------------------
export const login = async (username, password) => {
  const { data } = await client.post('auth/login/', { username, password });
  return data;
};

// Single sign-on: redeem a one-time handoff code minted by mavis_backend
// when the employee clicked "Helpdesk" in office.mavistech.uk's sidebar,
// instead of typing a username/password here.
export const ssoLogin = async (code) => {
  const { data } = await client.post('auth/sso-login/', { code });
  return data;
};

export const getMe = async () => {
  const { data } = await client.get('auth/me/');
  return data;
};

export const updateMyPreferences = async (payload) => {
  const { data } = await client.patch('auth/me/preferences/', payload);
  return data;
};

// --- Employees ----------------------------------------------------------
export const getEmployees = async () => {
  const { data } = await client.get('employees/');
  return data;
};

// --- Teams ----------------------------------------------------------------
export const getTeams = async () => {
  const { data } = await client.get('teams/');
  return data;
};

export const createTeam = async (payload) => {
  const { data } = await client.post('teams/create/', payload);
  return data;
};

export const updateTeam = async (teamId, payload) => {
  const { data } = await client.patch(`teams/${teamId}/update/`, payload);
  return data;
};

export const deleteTeam = async (teamId) => {
  await client.delete(`teams/${teamId}/delete/`);
};

// --- Email templates --------------------------------------------------------
export const getEmailTemplates = async (trigger) => {
  const { data } = await client.get('email-templates/', { params: trigger ? { trigger } : {} });
  return data;
};

export const createEmailTemplate = async (payload) => {
  const { data } = await client.post('email-templates/create/', payload);
  return data;
};

export const updateEmailTemplate = async (templateId, payload) => {
  const { data } = await client.patch(`email-templates/${templateId}/`, payload);
  return data;
};

export const deleteEmailTemplate = async (templateId) => {
  await client.delete(`email-templates/${templateId}/`);
};

export const setColumnEmailTemplate = async (columnId, templateId) => {
  const { data } = await client.post(`columns/${columnId}/email-template/`, { template_id: templateId });
  return data;
};

export const setColumnCreationEmailTemplate = async (columnId, templateId) => {
  const { data } = await client.post(`columns/${columnId}/creation-email-template/`, { template_id: templateId });
  return data;
};

export const setBoardDefaultStatusChangedTemplate = async (boardKey, templateId) => {
  const { data } = await client.post(`boards/${boardKey}/default-status-changed-template/`, { template_id: templateId });
  return data;
};

// --- Shipping profiles (Royal Mail weight/dimension presets) --------------
export const getShippingProfiles = async () => {
  const { data } = await client.get('shipping-profiles/');
  return data;
};

export const createShippingProfile = async (payload) => {
  const { data } = await client.post('shipping-profiles/create/', payload);
  return data;
};

export const updateShippingProfile = async (profileId, payload) => {
  const { data } = await client.patch(`shipping-profiles/${profileId}/`, payload);
  return data;
};

export const deleteShippingProfile = async (profileId) => {
  await client.delete(`shipping-profiles/${profileId}/`);
};

export const generateShipment = async (ticketId, payload) => {
  const { data } = await client.post(`tickets/${ticketId}/generate-shipment/`, payload);
  return data;
};

export const generateReturnLabel = async (ticketId, payload) => {
  const { data } = await client.post(`tickets/${ticketId}/generate-return-label/`, payload);
  return data;
};

// FREE preview -- runs a real Parcel2Go quote + real collection-dates
// lookup for the given address/weight/dimensions, but creates/pays for
// nothing. Backs the pickup-date dropdown in the shipment/return forms.
export const getShipmentOptions = async (ticketId, payload) => {
  const { data } = await client.post(`tickets/${ticketId}/shipment-options/`, payload);
  return data;
};

export const getReturnOptions = async (ticketId, payload) => {
  const { data } = await client.post(`tickets/${ticketId}/return-options/`, payload);
  return data;
};

// Re-fetches tracking number/label(s) for an already-paid shipment/return
// collection -- never creates or pays for anything new.
export const refreshShipment = async (ticketId) => {
  const { data } = await client.post(`tickets/${ticketId}/refresh-shipment/`);
  return data;
};

export const refreshReturnLabel = async (ticketId) => {
  const { data } = await client.post(`tickets/${ticketId}/refresh-return-label/`);
  return data;
};

// Parcel2Go has no cancel/refund API at all -- these only clear this
// app's own record of the shipment/return collection (so a fresh one can
// be generated) and email a staff reminder that the booking still needs
// cancelling directly on parcel2go.com.
export const cancelShipment = async (ticketId) => {
  const { data } = await client.post(`tickets/${ticketId}/cancel-shipment/`);
  return data;
};

export const cancelReturnLabel = async (ticketId) => {
  const { data } = await client.post(`tickets/${ticketId}/cancel-return-label/`);
  return data;
};

export const sendShippingInstructions = async (ticketId) => {
  const { data } = await client.post(`tickets/${ticketId}/send-shipping-instructions/`);
  return data;
};

export const cancelShippingInstructions = async (ticketId) => {
  const { data } = await client.post(`tickets/${ticketId}/cancel-shipping-instructions/`);
  return data;
};

// --- Sender accounts (send-only mailboxes for outbound email) -------------
export const getSenders = async () => {
  const { data } = await client.get('senders/');
  return data;
};

export const createSender = async (payload) => {
  const { data } = await client.post('senders/create/', payload);
  return data;
};

export const updateSender = async (senderId, payload) => {
  const { data } = await client.patch(`senders/${senderId}/`, payload);
  return data;
};

export const deleteSender = async (senderId) => {
  await client.delete(`senders/${senderId}/`);
};

// --- Unmatched email review queue ------------------------------------------
export const getUnmatchedEmails = async (includeResolved) => {
  const { data } = await client.get('unmatched-emails/', { params: includeResolved ? { resolved: 'true' } : {} });
  return data;
};

export const convertUnmatchedEmail = async (emailId, payload) => {
  const { data } = await client.post(`unmatched-emails/${emailId}/convert/`, payload || {});
  return data;
};

export const dismissUnmatchedEmail = async (emailId) => {
  const { data } = await client.post(`unmatched-emails/${emailId}/dismiss/`);
  return data;
};

export const replyToUnmatchedEmail = async (emailId, body, senderId, files = []) => {
  if (files.length === 0) {
    const { data } = await client.post(`unmatched-emails/${emailId}/reply/`, { body, sender_id: senderId || undefined });
    return data;
  }
  // Any files switches this to multipart -- same convention as addComment/
  // sendBroadcastEmail: the backend accepts both, FormData is only needed
  // to actually carry file bytes.
  const form = new FormData();
  form.append('body', body);
  if (senderId) form.append('sender_id', senderId);
  files.forEach((file) => form.append('attachments', file));
  const { data } = await client.post(`unmatched-emails/${emailId}/reply/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// Full email conversation (all inbound + replies) with the same sender.
export const getUnmatchedEmailHistory = async (emailId) => {
  const { data } = await client.get(`unmatched-emails/${emailId}/history/`);
  return data;
};

// --- Customers ------------------------------------------------------------
export const getCustomerEmailConversation = async (customerId) => {
  const { data } = await client.get(`customers/${customerId}/emails/`);
  return data;
};

export const getCustomerHistory = async (customerId) => {
  const { data } = await client.get(`customers/${customerId}/history/`);
  return data;
};

export const getCustomerDevices = async (customerId) => {
  const { data } = await client.get(`customers/${customerId}/devices/`);
  return data;
};

export const sendCustomerEmail = async (customerId, { subject, body, senderId, to }) => {
  const { data } = await client.post(`customers/${customerId}/send-email/`, { subject, body, sender_id: senderId || undefined, to: to || undefined });
  return data;
};

export const searchCustomers = async (query) => {
  const { data } = await client.get('customers/search/', { params: { q: query } });
  return data;
};

// Postcode -> address lookup (getAddress.io, proxied through mavis_backend
// and then this app's own backend) -- backs the "find your address"
// dropdown in CustomerPicker's new-account/adhoc forms and the
// shipment/return-collection address forms in TicketDetailModal.
export const lookupAddress = async (postcode) => {
  const { data } = await client.get(`customers/address-lookup/${encodeURIComponent(postcode.trim())}/`);
  return data;
};

export const quickCreateCustomer = async (payload) => {
  const { data } = await client.post('customers/quick-create/', payload);
  return data;
};

export const createAdhocCustomer = async (payload) => {
  const { data } = await client.post('customers/adhoc-create/', payload);
  return data;
};

export const getAllCustomers = async (query) => {
  const { data } = await client.get('customers/all/', { params: query ? { q: query } : {} });
  return data;
};

export const sendBroadcastEmail = async ({ customerIds, subject, body, files = [], senderId }) => {
  const form = new FormData();
  customerIds.forEach((id) => form.append('customer_ids', id));
  form.append('subject', subject);
  form.append('body', body);
  if (senderId) form.append('sender_id', senderId);
  files.forEach((file) => form.append('attachments', file));
  const { data } = await client.post('customers/broadcast-email/', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

// --- Boards ---------------------------------------------------------------
export const getBoards = async () => {
  const { data } = await client.get('boards/');
  return data;
};

export const getBoard = async (key) => {
  const { data } = await client.get(`boards/${key}/`);
  return data;
};

// --- Tickets ----------------------------------------------------------
export const getTickets = async (boardKey, q, { relation, showCompleted } = {}) => {
  const { data } = await client.get('tickets/', {
    params: {
      // Falsy (undefined/null/'') deliberately omits the board filter
      // entirely -- the backend then returns tickets across every board,
      // used by the List view's "All sections" mode.
      board: boardKey || undefined,
      q: q || undefined,
      relation: relation && relation !== 'all' ? relation : undefined,
      show_completed: showCompleted ? 'true' : undefined,
    },
  });
  return data;
};

export const getBoardCounts = async () => {
  const { data } = await client.get('tickets/board-counts/');
  return data;
};

export const getTicket = async (id) => {
  const { data } = await client.get(`tickets/${id}/`);
  return data;
};

export const createTicket = async (payload) => {
  const { data } = await client.post('tickets/create/', payload);
  return data;
};

export const updateTicket = async (id, payload) => {
  const { data } = await client.patch(`tickets/${id}/update/`, payload);
  return data;
};

export const deleteTicket = async (id) => {
  await client.delete(`tickets/${id}/delete/`);
};

export const moveTicket = async (id, columnId) => {
  const { data } = await client.post(`tickets/${id}/move/`, { column_id: columnId });
  return data;
};

export const assignTicket = async (id, employeeIds) => {
  const { data } = await client.post(`tickets/${id}/assign/`, { employee_ids: employeeIds });
  return data;
};

export const setReportTo = async (id, employeeIds) => {
  const { data } = await client.post(`tickets/${id}/report-to/`, { employee_ids: employeeIds });
  return data;
};

export const toggleTicketWorking = async (id) => {
  const { data } = await client.post(`tickets/${id}/toggle-working/`);
  return data;
};

export const getComments = async (id) => {
  const { data } = await client.get(`tickets/${id}/comments/`);
  return data;
};

export const addComment = async (id, { body, visibility = 'internal', is_customer_reply = false, tagged_employee_ids = [], files = [] }) => {
  if (files.length === 0) {
    const { data } = await client.post(`tickets/${id}/comments/`, { body, visibility, is_customer_reply, tagged_employee_ids });
    return data;
  }
  // Any files switches this to multipart -- the backend accepts both, but
  // FormData is required to actually carry file bytes.
  const form = new FormData();
  form.append('body', body);
  form.append('visibility', visibility);
  form.append('is_customer_reply', is_customer_reply ? 'true' : '');
  tagged_employee_ids.forEach((tid) => form.append('tagged_employee_ids', tid));
  files.forEach((file) => form.append('attachments', file));
  const { data } = await client.post(`tickets/${id}/comments/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const getAttachments = async (id) => {
  const { data } = await client.get(`tickets/${id}/attachments/`);
  return data;
};

export const uploadAttachment = async (id, file, visibility = 'internal') => {
  const form = new FormData();
  form.append('file', file);
  form.append('visibility', visibility);
  const { data } = await client.post(`tickets/${id}/attachments/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const deleteAttachment = async (ticketId, attachmentId) => {
  await client.delete(`tickets/${ticketId}/attachments/${attachmentId}/delete/`);
};

// --- Daily reports (end-of-day check-in) -----------------------------------
export const getDailyReportToday = async () => {
  const { data } = await client.get('daily-reports/today/');
  return data;
};

export const submitDailyReport = async ({ doneToday, planTomorrow }) => {
  const { data } = await client.post('daily-reports/submit/', { done_today: doneToday, plan_tomorrow: planTomorrow });
  return data;
};

export const getDailyReports = async ({ employeeId, dateFrom, dateTo } = {}) => {
  const { data } = await client.get('daily-reports/', {
    params: {
      employee_id: employeeId || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    },
  });
  return data;
};

// --- Priority alerts (pinned reminder banner) ------------------------------
export const getActiveAlerts = async () => {
  const { data } = await client.get('alerts/active/');
  return data;
};

export const createAlert = async (message, expiresAt) => {
  const { data } = await client.post('alerts/create/', { message, expires_at: expiresAt || undefined });
  return data;
};

export const dismissAlert = async (alertId) => {
  const { data } = await client.post(`alerts/${alertId}/dismiss/`);
  return data;
};

export const endAlert = async (alertId) => {
  const { data } = await client.post(`alerts/${alertId}/end/`);
  return data;
};

// --- Chat -------------------------------------------------------------
export const getChats = async () => {
  const { data } = await client.get('chats/');
  return data;
};

export const getChatUnreadCount = async () => {
  const { data } = await client.get('chats/unread-count/');
  return data;
};

export const startDirectChat = async (employeeId) => {
  const { data } = await client.post('chats/start-direct/', { employee_id: employeeId });
  return data;
};

export const createGroupChat = async (name, employeeIds) => {
  const { data } = await client.post('chats/create-group/', { name, employee_ids: employeeIds });
  return data;
};

export const getChatMessages = async (conversationId) => {
  const { data } = await client.get(`chats/${conversationId}/messages/`);
  return data;
};

export const postChatMessage = async (conversationId, { body, taggedEmployeeIds = [], files = [] }) => {
  if (files.length === 0) {
    const { data } = await client.post(`chats/${conversationId}/messages/`, { body, tagged_employee_ids: taggedEmployeeIds });
    return data;
  }
  // Any files switches this to multipart, same pattern as addComment.
  const form = new FormData();
  form.append('body', body || '');
  taggedEmployeeIds.forEach((tid) => form.append('tagged_employee_ids', tid));
  files.forEach((file) => form.append('attachments', file));
  const { data } = await client.post(`chats/${conversationId}/messages/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const addChatParticipants = async (conversationId, employeeIds) => {
  const { data } = await client.post(`chats/${conversationId}/participants/add/`, { employee_ids: employeeIds });
  return data;
};

export const leaveChat = async (conversationId) => {
  const { data } = await client.post(`chats/${conversationId}/leave/`);
  return data;
};

// --- Notifications ------------------------------------------------------
export const getNotifications = async () => {
  const { data } = await client.get('notifications/');
  return data;
};

export const getUnreadCount = async () => {
  const { data } = await client.get('notifications/unread-count/');
  return data;
};

export const markNotificationsRead = async () => {
  const { data } = await client.post('notifications/mark-read/');
  return data;
};


// --- Working day (start = first helpdesk open of the day, finish = daily report) ---
export const workdayStart = async () => {
  const { data } = await client.post('workday/start/');
  return data;
};

export const workdayPing = async () => {
  const { data } = await client.post('workday/ping/');
  return data;
};

export const getAttendance = async ({ dateFrom, dateTo } = {}) => {
  const { data } = await client.get('admin/attendance/', { params: { date_from: dateFrom || undefined, date_to: dateTo || undefined } });
  return data;
};
